import CreateIcon from "@material-ui/icons/CreateTwoTone";
import { default as PlayListAddCheck } from "@material-ui/icons/PlaylistAddCheck";
import Alert from "@material-ui/lab/Alert";
import { TSubmitValues } from "backend/datainput";
import useComments from "backend/hooks/comments/useComments";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useAttachments from "backend/hooks/files/useAttachments";
import useTopElements from "backend/hooks/model/useTopElements";
import useMyWorkflows from "backend/hooks/workflows/useMyWorkflows";
import useSubmitElementValues from "backend/hooks/workflows/useSubmitElementValues";
import { getWorkflowInstance } from "backend/model/getWorkflowInstance";
import {
  ICardElementWithChildren,
  TWorkflowInstance,
} from "backend/model/model";
import WorkflowPagination from "components/WorkflowPagination";
import InitiativeApproveForm from "components/forms/valueinput/InitiativeApproveForm";
import InitiativeInputForm from "components/forms/valueinput/InitiativeInputForm";
import KPIApproveForm from "components/forms/valueinput/KPIApprove";
import KPIInputForm from "components/forms/valueinput/KPIInputForm";
import ElementLink from "components/link/ElementLink";
import WorkflowToolbar from "components/toolbars/WorkflowToolbar";
import WorkflowElementState from "components/workflow/WorkflowElementState";
import WorkflowLogs from "components/workflow/WorkflowLogs";
import WorkflowSteps from "components/workflow/WorkflowSteps";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import { YearEndTargetCard } from "design/components/cards/KPIPerformanceCard";
import CardCheckedBox from "design/components/checkboxes/CardCheckedBox";
import PeriodChip from "design/components/chips/PeriodChip";
import WorkflowInputTable from "design/components/tables/WorkflowInputTable";
import DashboardTitle from "design/components/texts/DashboardTitle";
import ExecutiveDashboardTitle from "design/components/texts/ExecutiveDashboardTitle";
import PageContainer from "design/layouts/PageContainer";
import RowView from "design/layouts/RowView";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import AlertMessage from "qpr-base/ui/components/AlertMessage";
import UploadedFilesList from "qpr-base/ui/components/UploadedFilesList";
import React from "react";
import { If } from "react-if";
import { useLocation } from "react-router-dom";

const WorkflowInput = () => {
  const ctx = useAppContext();
  const ref = React.useRef<any>();
  const { pathname } = useLocation();
  const link = useLink();
  const [page, setPage] = React.useState(0);
  const [steps, setSteps] = React.useState<any>([]);
  const [selectedElement, setSelectedElement] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);
  const { pagination: usePaging, rowsPerPage } =
    conf.getWorkflowInputViewSettings();
  const pathArr = pathname.split("/");
  let selectedElementId = pathArr[pathArr.length - 1];

  const { data: topElements, isLoading: isLoadingTopElements } =
    useTopElements();

  React.useEffect(() => {
    setSelected(selectedElementId);
  }, [selectedElementId]);

  const selectRow = React.useCallback(
    (item: ICardElementWithChildren | undefined) => {
      link({ meaId: item?.workflowInstance?.wfInstanceId });
    },
    [link]
  );

  const { data: attachments, isLoading: isLoadingAttachments } = useAttachments(
    {
      elementId: selectedElement?.id || "",
      period: selectedElement?.workflowInstance?.period || "",
    }
  );

  React.useEffect(() => {
    if (page > 0)
      setTimeout(() => {
        if (ref?.current) ref.current.scrollIntoView(false);
      }, 700);
  }, [page]);

  const getWorkflowSteps = (workflowInstance: any) => {
    if (!workflowInstance?.configuration) return [];
    const wfConfigObj = JSON.parse(workflowInstance.configuration);
    const steps: any[] = [];
    let currentState = wfConfigObj.states[wfConfigObj.initial];
    let stateKey = wfConfigObj.initial;
    while (currentState.type !== "final") {
      steps.push({ currentState, ...{ state: stateKey } });
      stateKey =
        currentState.on["SUBMIT"]?.target || currentState.on["APPROVE"]?.target;
      currentState = wfConfigObj.states[stateKey];
    }
    const finalSteps = steps.map((step) => {
      return {
        title: step.currentState.on["SUBMIT"]
          ? t("WorkflowSteps.Data Input")
          : t("WorkflowSteps.Approval"),
        state: step.state,
      };
    });
    return finalSteps;
  };

  const { data: myWorkflows, isLoading: isLoadingWorkflows } = useMyWorkflows(
    usePaging ? page || 1 : undefined,
    usePaging ? rowsPerPage : undefined,
    usePaging && page === 0 ? selectedElementId : ""
  );

  const saveValues = useSubmitElementValues();

  const { data: comments, isLoading: isLoadingComments } = useComments({
    id: selectedElement?.workflowInstance?.meaId || "",
    period: selectedElement?.workflowInstance?.period || "",
  });

  const [firstComment] = comments ? comments : [];

  const { data: selectedRowWithChildren, isLoading: isLoadingHierarchy } =
    useHierarchy(
      selectedElement?.id || "",
      1,
      selectedElement?.workflowInstance?.period || "",
      selectedElement?.childobjects === ""
    );

  const setSelected = async (selectedElement: string) => {
    if (selectedElement === "") return;
    const wf = await getWorkflowInstance(selectedElement);
    const stepsArr = getWorkflowSteps(wf?.workflowInstance);
    setSteps(stepsArr);
    setSelectedElement(wf);
  };

  const saveValuesAndMakeWorkflowTransition = async (values: TSubmitValues | TSubmitValues["fields"]) => {
    const payload: TSubmitValues = Array.isArray(values)
      ? { fields: values, transition: "SUBMIT" }
      : values;

    payload.wf = selectedElement?.workflowInstance;

    await saveValues.mutate(payload, {
      onSuccess: (result: any) => {
        const wfInstanceAfterTransition: TWorkflowInstance = result.data;
        if (wfInstanceAfterTransition.nextTransitions?.length === 0)
          selectRow(undefined);
        setSelected(selectedElementId);
      },
    });
  };

  const hasAllowedActions =
    selectedElement?.workflowInstance?.allowedActions.length;
  const isDataEntryPhase = ["approvalrejected", "dataentry", "update"].includes(
    selectedElement?.workflowInstance?.currentState || ""
  );
  const canReject =
    selectedElement?.workflowInstance?.allowedActions.includes("reject");
  const canApprove =
    selectedElement?.workflowInstance?.allowedActions.includes("approve");
  const isApprovalPhase =
    !isDataEntryPhase &&
    ["finalapprovalrejected", "approval", "finalapproval"].includes(
      selectedElement?.workflowInstance?.currentState || ""
    );
  const getTaskCount = (): number => {
    if (!myWorkflows?.length) return 0;
    return myWorkflows[0].workflowInstance?.totalWorkflowCount || 0;
  };

  const getPage = (): number => {
    if (page) return page;
    if (!myWorkflows?.length) return 0;
    return myWorkflows[0].workflowInstance?.page || 0;
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: any) => {
    setPage(value);
  };

  const isEscalated = (wf: TWorkflowInstance) => {
    const wfItem = myWorkflows?.find(
      (item) => item.workflowInstance?.wfInstanceId === wf?.wfInstanceId
    );
    if (!wfItem || !wfItem.workflowInstance) return false;

    return (
      wfItem.workflowInstance.escalations?.find(
        (esc) => esc.state === wf.currentState
      ) !== undefined
    );
  };

  const getSeverity = (
    wf?: TWorkflowInstance
  ): "error" | "info" | "warning" | "success" => {
    if (wf && isEscalated(wf)) return "error";
    if (
      wf?.currentState === "dataentry" ||
      wf?.currentState === "update" ||
      wf!.currentState === "approvalrejected"
    )
      return "info";
    return "success";
  };

  const getIcon = (wf?: TWorkflowInstance): React.ReactNode => {
    if (
      wf?.currentState === "dataentry" ||
      wf?.currentState === "update" ||
      wf!.currentState === "approvalrejected"
    )
      return <CreateIcon />;
    if (wf?.currentState.includes("approval")) return <PlayListAddCheck />;
    return undefined;
  };

  const isWorkflowCompleted = (): boolean => {
    return selectedElement?.workflowInstance?.nextTransitions?.length === 0;
  };

  const getTargetCardSettings = (element: ICardElementWithChildren) => {
    return {
      title: "workflowinput.form.Actual",
      value: { attribute: "ACT" },
      unit: { attribute: "ACT_unit" },
    };
  };

  if (!selectedElementId && myWorkflows && myWorkflows.length > 0)
    selectRow(myWorkflows[0]);

  return (
    <Page
      Toolbar={WorkflowToolbar}
      isLoading={
        isLoadingHierarchy ||
        isLoadingWorkflows ||
        isLoadingAttachments ||
        isLoadingComments ||
        isLoadingTopElements ||
        !selectedElement ||
        saveValues.isLoading
      }
    >
      {!myWorkflows || myWorkflows?.length === 0 ? (
        <PageContainer>
          <ExecutiveDashboardTitle>
            {t(
              "workflowinput.Alert.At the moment there are no Workflows to manage."
            )}
          </ExecutiveDashboardTitle>
        </PageContainer>
      ) : (
        <SplitView>
          <div>
            <WorkflowInputTable
              keyFn={(row) =>
                row?.workflowInstance?.wfInstanceId || row?.id || ""
              }
              selectedRow={selectedElement}
              items={myWorkflows || []}
              columns={[
                {
                  title: t("workflowinput.table.Done").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) =>
                    Number(a.workflowInstance?.done || 0) -
                    Number(b.workflowInstance?.done || 0),
                  value: (item) => {
                    return (
                      <div>
                        <CardCheckedBox
                          dx={0}
                          dy={-30}
                          type="blue"
                          isOpen={item.elem.workflowInstance?.done}
                        />
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.Name").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) => a.name.localeCompare(b.name),
                  value: (item) => {
                    return (
                      <div>
                        <div>
                          {getTranslatedAttribute(
                            item.elem,
                            "Name",
                            ctx.language
                          )}
                        </div>
                        <div>
                          <i>
                            {getTranslatedAttribute(
                              topElements?.find(
                                (topElement) =>
                                  topElement.name === item.elem.scorecardname
                              ),
                              "Name",
                              ctx.language
                            )}
                          </i>
                        </div>
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.Type").toString(),
                  onRowClick: selectRow,
                  value: (item) => {
                    return <div>{t("configtypes." + item.elem.typename)}</div>;
                  },
                },
                {
                  title: t("workflowinput.table.State").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) =>
                    a.workflowInstance?.currentState.localeCompare(
                      b.workflowInstance?.currentState || ""
                    ) || 0,
                  value: (item) => {
                    return (
                      <div
                        style={{
                          minWidth: "150px",
                        }}
                      >
                        <AlertMessage
                          severity={getSeverity(item.elem.workflowInstance)}
                          icon={getIcon(item.elem.workflowInstance)}
                        >
                          {t(
                            "workflowinput.Alert." +
                              item.elem.workflowInstance?.message
                          ) || ""}
                        </AlertMessage>
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.Period").toString(),
                  onRowClick: selectRow,
                  sortFn: (a, b) =>
                    (a.workflowInstance?.period || "").localeCompare(
                      b.workflowInstance?.period || ""
                    ),
                  value: (item) => {
                    return (
                      <div
                        style={{
                          maxWidth: "100px",
                        }}
                      >
                        {item.elem.period?.name}
                      </div>
                    );
                  },
                },
                {
                  title: t("workflowinput.table.ACT").toString(),
                  onRowClick: selectRow,
                  value: (item) => {
                    return <div>{item.elem.ACT_prettyvalue}</div>;
                  },
                },
              ]}
            />
            {usePaging && getTaskCount() > (rowsPerPage || 6) && (
              <WorkflowPagination
                ref={ref}
                page={getPage()}
                caption={t("navItems.Workflows")}
                rowsPerPage={rowsPerPage || 6}
                totalItemCount={getTaskCount()}
                onChange={handlePageChange}
              />
            )}
          </div>
          <StackView padding="20px">
            {selectedElement?.workflowInstance?.delegates &&
            selectedElement?.workflowInstance?.delegates.length > 0 ? (
              <Alert>
                {selectedElement?.workflowInstance?.delegates
                  ?.map((item) => item.message)
                  .pop()}
                <div>
                  <i style={{ fontSize: "0.9em" }}>
                    {selectedElement?.workflowInstance?.delegates
                      ?.map((item) => item.senderFullname)
                      .pop()}
                  </i>
                </div>
              </Alert>
            ) : null}
            {isWorkflowCompleted() ? (
              <>
                {false ? (
                  <>
                    <DashboardTitle>
                      {getTranslatedAttribute(
                        selectedElement,
                        "Name",
                        ctx.language
                      )}{" "}
                      <ElementLink item={selectedElement} />
                    </DashboardTitle>

                    <div style={{ width: "140px" }}>
                      <PeriodChip
                        period={selectedElement?.workflowInstance?.period || ""}
                        icon="KPI"
                      />
                    </div>
                  </>
                ) : null}
                <Alert severity="success">
                  {t("workflowinput.Alert.Workflow was Completed")}
                </Alert>
              </>
            ) : null}
            {saveValues.error ? (
              <Alert severity="warning">{String(saveValues.error)}</Alert>
            ) : null}
            <If condition={!!selectedElement}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <DashboardTitle>
                  {getTranslatedAttribute(
                    selectedElement,
                    "Name",
                    ctx.language
                  )}{" "}
                  <ElementLink item={selectedElement} />
                </DashboardTitle>
                <div style={{ width: "140px", marginTop: "-10px" }}>
                  <PeriodChip
                    period={selectedElement?.workflowInstance?.period || ""}
                    icon="KPI"
                  />
                </div>
              </div>
            </If>

            {false ? (
              <Alert severity="error">
                {t("workflowinput.Alert.Could not change the workflow state")}
              </Alert>
            ) : null}

            {steps.length > 1 ? (
              <WorkflowSteps
                workflowId={selectedElement?.workflowInstance?.wfInstanceId}
                currentState={
                  selectedElement?.workflowInstance?.currentState || ""
                }
                showSteps={steps}
              />
            ) : null}
            <WorkflowElementState
              item={myWorkflows.find(
                (item) =>
                  item.workflowInstance?.wfInstanceId ===
                  selectedElement?.workflowInstance?.wfInstanceId
              )}
            />
            <If
              condition={
                !isWorkflowCompleted() && !!selectedElement && !isDataEntryPhase
              }
            >
              {selectedElement?.typename === "KPI" && !isApprovalPhase && (
                <RowView gap="20px">
                  <YearEndTargetCard
                    element={selectedElement}
                    settings={getTargetCardSettings(selectedElement)}
                  />
                  <If condition={!!firstComment}>
                    <AnalysisCard
                      userData={{
                        name:
                          firstComment?.modifiedby || firstComment?.createdby,
                        subtitle: "Owner",
                      }}
                      date={new Date(
                        firstComment?.lastmodifieddate
                      ).toLocaleDateString()}
                      description={
                        firstComment
                          ? firstComment.description
                          : "No Comments yet"
                      }
                    >
                      <UploadedFilesList values={attachments} readOnly />
                    </AnalysisCard>
                  </If>
                </RowView>
              )}
            </If>
            <If
              condition={
                !isWorkflowCompleted() &&
                hasAllowedActions &&
                isApprovalPhase &&
                !!selectedElement
              }
            >
              {selectedElement?.typename === "Initiative" ? (
                <InitiativeApproveForm
                  key={selectedElement?.workflowInstance?.wfInstanceId}
                  initiative={selectedElement!}
                  childElements={selectedRowWithChildren || []}
                  comments={comments}
                  attachments={attachments}
                  canApprove={canApprove}
                  canReject={canReject}
                  onSubmit={(values) => {
                    saveValuesAndMakeWorkflowTransition(values);
                  }}
                />
              ) : null}
              {selectedElement?.typename === "KPI" ? (
                <KPIApproveForm
                  key={selectedElement?.workflowInstance?.wfInstanceId}
                  kpi={selectedElement!}
                  childElements={selectedRowWithChildren || []}
                  comments={comments}
                  attachments={attachments}
                  canApprove={canApprove}
                  canReject={canReject}
                  onSubmit={(values) => {
                    saveValuesAndMakeWorkflowTransition(values);
                  }}
                />
              ) : null}
            </If>
            {selectedElement?.workflowInstance?.allowedActions.includes(
              "editvalues"
            ) &&
            isDataEntryPhase &&
            hasAllowedActions &&
            !!selectedElement ? (
              <>
                <If condition={selectedElement?.typename === "KPI"}>
                  <KPIInputForm
                    kpi={selectedElement!}
                    childElements={selectedRowWithChildren || []}
                    comments={comments}
                    attachments={attachments}
                    onSubmit={(values) => {
                      saveValuesAndMakeWorkflowTransition({
                        fields: values,
                        transition: "SUBMIT",
                      });
                    }}
                  />
                </If>
                <If condition={selectedElement?.typename === "Initiative"}>
                  <InitiativeInputForm
                    initiative={selectedElement!}
                    childElements={selectedRowWithChildren || []}
                    comments={comments}
                    attachments={attachments}
                    translatedName={getTranslatedAttribute(
                      selectedElement,
                      "Name",
                      ctx.language
                    )}
                    onSubmit={(values) => {
                      saveValuesAndMakeWorkflowTransition({
                        fields: values,
                        transition: "SUBMIT",
                      });
                    }}
                  />
                </If>
              </>
            ) : null}

            <WorkflowLogs
              workflowId={selectedElement?.workflowInstance?.wfInstanceId}
            />
          </StackView>
        </SplitView>
      )}
    </Page>
  );
};

export default WorkflowInput;
